import { useRef } from "react";
import { Column } from "primereact/column";

import jsPDF from "jspdf";
import "jspdf-autotable";
import mboxLogo from "../../../../components/icons/logo/mbox_logo.jpg";
import {
  formatCurrencyValue,
  formatDatewithTime,
} from "../../../../utils/formatters";
import { formatDateYYYYMMDD } from "../../../../algorithms/date";
import { DataTable } from "primereact/datatable";
import { DownloadButton } from "../../../../components/core";

const PartnerWalletTable = ({
  data = [],
  headerClassName = "",
  searchResultHeader = null,
  filterValues = {},
  allPartners,
  allBranches,
  allAgents,
  operatorName,
}) => {
  const tableRef = useRef(null);

  const dateBody = (rowData) => (
    <div className="text-sm text-gray-900 py-3">
      {formatDatewithTime(rowData.transactionDate)}
    </div>
  );

  const transactionReferenceBody = (rowData) => (
    <div className="text-sm font-medium text-gray-900 py-3">
      {rowData.transactionReferenceNo}
    </div>
  );

  const transactionTypeBody = (rowData) => (
    <div className="text-sm text-gray-900 py-3">{rowData.transactionType}</div>
  );

  const detailsBody = (rowData) => (
    <div className="text-sm font-medium py-3">{rowData.detailsName}</div>
  );

  const debitBody = (rowData) => (
    <div className="text-sm font-semibold text-red-600 text-right py-3">
      {rowData.debit ? `(- ${formatCurrencyValue(rowData.debit)})` : "-"}
    </div>
  );

  const creditBody = (rowData) => (
    <div className="text-sm font-semibold text-green-600 text-right py-3">
      {rowData.credit ? `(+ ${formatCurrencyValue(rowData.credit)})` : "-"}
    </div>
  );

  const balanceBody = (rowData) => (
    <div className="text-sm font-semibold text-gray-900 text-right py-3">
      {formatCurrencyValue(rowData.balance)}
    </div>
  );
  const handlePDFDownload = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 10;

    // Get account name based on filter values
    const getAccountName = () => {
      // Priority order: selectedPartnerAgent > selectedWallet > accountId > default

      if (filterValues.walletFilter == 1) {
        return operatorName;
      } else if (filterValues.walletFilter == 2) {
        if (filterValues.selectedPartnerId) {
          const partner = allPartners.find(
            (p) => p.value == filterValues.selectedPartnerId,
          );

          return partner?.name;
        }
      } else if (filterValues.walletFilter == 3) {
        if (filterValues.selectedPartnerBranch) {
          const branch = allBranches.find(
            (b) => b.value == filterValues.selectedPartnerBranch,
          );

          return branch?.name;
        }
      } else {
        if (filterValues.selectedPartnerAgent) {
          const agent = allAgents.find(
            (p) => p.value == filterValues.selectedPartnerAgent,
          );

          console.log({ agent, filterValues });
          return agent?.name;
        }
      }

      // if (
      //   filterValues?.selectedWallet &&
      //   filterValues.selectedWallet.length > 0
      // ) {
      //   return filterValues.selectedWallet[0].label;
      // }
      // if (filterValues?.accountId) {
      //   return filterValues.accountId;
      // }
      return "[ACCOUNT NAME]";
    };

    const accountName = getAccountName();

    // Load and add imported logo (handles transparent PNGs)
    try {
      const img = new Image();
      img.src = mboxLogo;

      img.onload = async () => {
        // Convert image to canvas then to base64 (PNG to preserve transparency)
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const imgData = canvas.toDataURL("image/png");

        // Add image to PDF (wider)
        doc.addImage(imgData, "PNG", 15, yPosition - 2, 30, 15);

        addContent();
      };
    } catch (error) {
      console.warn("Logo not found, skipping logo image");
      addContent();
    }

    const addContent = () => {
      // Add "Statement of Account" (right side)
      doc.setFontSize(9);
      doc.setFont(undefined, "bold");
      doc.text("Statement of Account", pageWidth - 15, yPosition + 4, {
        align: "right",
      });

      yPosition += 12;

      // Add account details (left side)
      doc.setFontSize(8);
      doc.setFont("Montserrat", "normal");
      doc.text("PARTNER WALLET", 15, yPosition + 8);

      // Add statement period (right side)
      doc.text("Statement Period", pageWidth - 15, yPosition + 8, {
        align: "right",
      });

      yPosition += 4;
      doc.setFontSize(7);
      doc.text(`Account Name: ${accountName}`, 15, yPosition + 8);
      doc.text(
        `${formatDateYYYYMMDD(filterValues.startDate)} to ${formatDateYYYYMMDD(
          filterValues.endDate,
        )}`,
        pageWidth - 15,
        yPosition + 8,
        {
          align: "right",
        },
      );

      yPosition += 8;

      // Prepare table data
      const tableData = data.map((row) => [
        formatDatewithTime(row.transactionDate),
        row.transactionReferenceNo,
        row.transactionType,
        row.detailsName.replace(/→/g, "->").substring(0, 50),
        "-",
        row.debit ? `${row.debit.toFixed(2)}` : "-",
        row.credit ? `${row.credit.toFixed(2)}` : "-",
        row.balance ? `${row.balance.toFixed(2)}` : "-",
      ]);

      // Add table
      doc.autoTable({
        head: [
          [
            "Date",
            "Transaction Reference No",
            "Transaction Type",
            "Details",
            "Remarks",
            "Debit",
            "Credit",
            "Balance",
          ],
        ],
        body: tableData,
        startY: yPosition + 8,
        margin: { top: 10, right: 10, bottom: 10, left: 10 },
        headStyles: {
          fontSize: 7,
          fontStyle: "bold",
          fillColor: [254, 205, 8],
          textColor: [0, 0, 0],
        },
        bodyStyles: {
          fontSize: 6,
          cellPadding: 3,
          valign: "top",
          overflow: "linebreak",
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "center" },
          2: { halign: "center" },
          3: { halign: "left" },
          4: { halign: "center" },
          5: { halign: "right" },
          6: { halign: "right" },
          7: { halign: "right" },
        },
        didDrawPage: (data) => {
          const pageCount = doc.getNumberOfPages();
          const pageHeight = doc.internal.pageSize.getHeight();
          const footerY = pageHeight - 10;

          doc.setFontSize(7);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageWidth / 2,
            footerY,
            { align: "center" },
          );
        },
      });

      doc.save("partner_wallet_history.pdf");
    };
  };

  return (
    <>
      <div
        className={
          "text-center pt-3 bg-primary-primaryYellow text-secondary-purpleGray p-2"
        }
      >
        <span>
          <h1 className={"font-bold"}>PARTNER WALLET HISTORY REPORT</h1>
        </span>
        <span>
          <h1 className={"font-bold"}>{searchResultHeader}</h1>
        </span>
      </div>

      {/* {filterValues.selectedPartnerBranch?.length < 1 && ( */}
      <DownloadButton
        pngOnly={false}
        csvOnly={false}
        specialCase={true}
        pdfOnly={true}
        onPdfClick={handlePDFDownload}
        // containerBgColor="bg-primary-primaryDarkGray"
        id="partner-wallet-table"
      />

      <div id="partner-wallet-table">
        <DataTable value={data} stripedRows dt={tableRef}>
          <Column
            field="transactionDate"
            header="Date"
            body={dateBody}
            alignHeader={"center"}
            headerClassName={headerClassName}
            style={{ width: "14%" }}
          />
          <Column
            field="transactionReferenceNo"
            header="Transaction Reference No"
            body={transactionReferenceBody}
            alignHeader={"center"}
            headerClassName={headerClassName}
            style={{ width: "13%" }}
          />
          <Column
            field="transactionType"
            header="Transaction Type"
            body={transactionTypeBody}
            alignHeader={"center"}
            headerClassName={headerClassName}
            style={{ width: "12%" }}
          />
          <Column
            field="detailsName"
            header="Details"
            body={detailsBody}
            alignHeader={"center"}
            headerClassName={headerClassName}
            style={{ width: "18%" }}
          />
          <Column
            header="Remarks"
            body={() => <div className="py-3">-</div>}
            alignHeader={"center"}
            headerClassName={headerClassName}
            style={{ width: "8%" }}
          />
          <Column
            field="debit"
            header="Debit"
            body={debitBody}
            alignHeader={"center"}
            headerClassName={headerClassName}
            style={{ width: "12%" }}
          />
          <Column
            field="credit"
            header="Credit"
            body={creditBody}
            alignHeader={"center"}
            headerClassName={headerClassName}
            style={{ width: "12%" }}
          />
          <Column
            field="balance"
            header="Balance"
            body={balanceBody}
            alignHeader={"center"}
            headerClassName={headerClassName}
            style={{ width: "11%" }}
          />
        </DataTable>
      </div>
    </>
  );
};

export default PartnerWalletTable;
